import { collection, getDocs, writeBatch } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { db, storage } from '../config/firebase';

const BATCH_LIMIT = 500;

const deleteCollectionInBatches = async (pathSegments: string[]) => {
  while (true) {
    const snapshot = await getDocs(collection(db, ...pathSegments));

    if (snapshot.empty) {
      break;
    }

    const batch = writeBatch(db);
    snapshot.docs.slice(0, BATCH_LIMIT).forEach((docSnapshot) => {
      batch.delete(docSnapshot.ref);
    });

    await batch.commit();

    if (snapshot.size < BATCH_LIMIT) {
      break;
    }
  }
};

const collectStoragePaths = async (pathSegments: string[]) => {
  const snapshot = await getDocs(collection(db, ...pathSegments));
  const storagePaths = new Set<string>();

  snapshot.docs.forEach((docSnapshot) => {
    const data = docSnapshot.data() as { storagePath?: string };
    if (typeof data.storagePath === 'string' && data.storagePath.trim().length > 0) {
      storagePaths.add(data.storagePath);
    }
  });

  return storagePaths;
};

const deleteStoredPayloads = async (storagePaths: Set<string>) => {
  if (storagePaths.size === 0) return;

  await Promise.allSettled(
    Array.from(storagePaths).map((path) => deleteObject(ref(storage, path))),
  );
};

export const clearConversationMessages = async (conversationId: string) => {
  const [sessionPaths, conversationPaths] = await Promise.all([
    collectStoragePaths(['sessions', conversationId, 'messages']),
    collectStoragePaths(['conversations', conversationId, 'messages']),
  ]);

  const allPaths = new Set<string>([...sessionPaths, ...conversationPaths]);

  await Promise.all([
    deleteCollectionInBatches(['sessions', conversationId, 'messages']),
    deleteCollectionInBatches(['conversations', conversationId, 'messages']),
  ]);

  await deleteStoredPayloads(allPaths);
};
